import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Layout from "@/components/Layout";
import StatusBadge from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import api from "@/lib/api";
import { FileText, Plus, BellRing, ShieldCheck, UserCog } from "lucide-react";

export default function BorrowerDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/dashboard/borrower").then((r) => setData(r.data)).finally(() => setLoading(false));
  }, []);

  if (loading) return <Layout><div className="text-slate-500">Loading…</div></Layout>;

  const apps = data?.applications || [];
  const active = apps.find((a) => !["completed", "cancelled", "rejected"].includes(a.status));

  return (
    <Layout>
      <div className="fade-in space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-slate-900" style={{fontFamily:'Outfit'}}>Your dashboard</h1>
            <p className="text-slate-600 text-sm mt-1">Track applications, complete eKYC, and manage repayments.</p>
          </div>
          <Link to="/borrower/apply" className="w-full sm:w-auto">
            <Button className="w-full sm:w-auto bg-blue-900 hover:bg-blue-800" data-testid="new-application-btn"><Plus className="h-4 w-4 mr-1"/> New application</Button>
          </Link>
        </div>

        <div className="grid sm:grid-cols-3 gap-4">
          <KPI icon={FileText} label="Applications" value={apps.length} testId="kpi-apps"/>
          <KPI icon={ShieldCheck} label="eKYC status" value={String(data.ekyc_status).replaceAll('_',' ')} testId="kpi-ekyc"/>
          <KPI icon={BellRing} label="Unread notifications" value={data.unread_notifications} testId="kpi-notifs"/>
        </div>

        {!data.profile_complete && (
          <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900 flex items-start gap-2" data-testid="profile-incomplete-banner">
            <UserCog className="h-4 w-4 mt-0.5"/>
            <div>Complete your profile during your next application to unlock submission.</div>
          </div>
        )}

        <Card className="rounded-xl border-slate-200">
          <CardContent className="p-0">
            <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
              <h2 className="font-semibold text-slate-900">Your applications</h2>
            </div>
            {apps.length === 0 ? (
              <div className="px-6 py-10 text-center text-slate-500" data-testid="no-applications">
                No applications yet. Start your first one.
              </div>
            ) : (
              <ul>
                {apps.map((a) => (
                  <li key={a.id} className="px-4 sm:px-6 py-4 border-b last:border-b-0 border-slate-100 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3" data-testid={`app-row-${a.id}`}>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-slate-900">{a.product_name}</span>
                        <StatusBadge status={a.status}/>
                      </div>
                      <div className="text-sm text-slate-500 mt-0.5">${a.amount.toFixed(2)} · {a.duration} {a.duration_unit} · {new Date(a.created_at).toLocaleDateString()}</div>
                    </div>
                    <Link to={`/borrower/applications/${a.id}`} className="w-full sm:w-auto"><Button variant="outline" size="sm" className="w-full sm:w-auto" data-testid={`open-app-${a.id}`}>Open</Button></Link>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}

function KPI({ icon: Icon, label, value, testId }) {
  return (
    <Card className="rounded-xl border-slate-200" data-testid={testId}>
      <CardContent className="p-5 flex items-center gap-3">
        <div className="h-10 w-10 rounded-lg bg-blue-50 text-blue-900 grid place-items-center"><Icon className="h-5 w-5"/></div>
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500">{label}</div>
          <div className="text-xl font-semibold text-slate-900 capitalize">{value}</div>
        </div>
      </CardContent>
    </Card>
  );
}
